import React, { ReactNode } from "react";
import { RichTextElement } from "@kontent-ai/gatsby-components";
import { graphql, Link } from "gatsby";
import classnames from "classnames";
import Button, { Theme, Rank } from "@unum/button";
import styles from "../styles/RichText.module.scss";
import { getClickableAction } from "./Link";
import { RichTextFragmentFragment } from "../../graphql-types";
import Heading from "@unum/heading";
import SingleImage from "./SingleImage";
import DoubleImage from "./DoubleImage";
import Quote from "./Quote";
import SimpleSection from "./SimpleSection";
import TAndCSection from "./TAndCSection";
import ExternalVideo from "./ExternalVideo";

export type Settings = {
  background: "light" | "dark";
  buttonTheme: Theme;
  rank: Rank;
};

type Props = {
  codename?: string;
  data: any;
  settings?: Partial<Settings>;
  hasUnrestyledHeadings?: boolean;
  className?: string;
};

const removeOuterStrongElement = (domNode) => {
  if (domNode[0]?.name === "strong") {
    return domNode[0]?.children;
  }
  return domNode;
};

const resolveDomNode = (
  domNode,
  domToReact,
  hasUnrestyledHeadings?: boolean
): ReactNode => {
  if (!hasUnrestyledHeadings) {
    switch (domNode?.name) {
      case "h2":
        return (
          <Heading level={3}>
            {domToReact(removeOuterStrongElement(domNode?.children))}
          </Heading>
        );
      case "h3":
        return (
          <Heading level={4}>
            {domToReact(removeOuterStrongElement(domNode?.children))}
          </Heading>
        );
      case "h4":
        return (
          <Heading level={5}>
            {domToReact(removeOuterStrongElement(domNode?.children))}
          </Heading>
        );
      case "h5":
        return (
          <Heading level={6}>
            {domToReact(removeOuterStrongElement(domNode?.children))}
          </Heading>
        );
      default:
        return domToReact(domNode);
    }
  }

  return domToReact(domNode);
};

const resolveLinkedItem = (linkedItem, settings) => {
  if (!linkedItem) {
    return null;
  }

  if (
    linkedItem.__typename === "kontent_item_internal_link" ||
    linkedItem.__typename === "kontent_item_external_link"
  ) {
    const rank =
      settings?.rank ||
      (settings?.background === "dark" ? "tertiary" : "primary");

    return (
      <Button
        rank={rank}
        theme={settings?.buttonTheme}
        action={getClickableAction(linkedItem)}
        className={styles[`${rank}`]}
      >
        {linkedItem.elements.label.value}
      </Button>
    );
  } else if (linkedItem.__typename === "kontent_item_single_image") {
    return <SingleImage data={linkedItem} />;
  } else if (linkedItem.__typename === "kontent_item_double_image") {
    return <DoubleImage data={linkedItem} />;
  } else if (linkedItem.__typename === "kontent_item_quote") {
    return <Quote data={linkedItem} />;
  } else if (linkedItem.__typename === "kontent_item_simple_section") {
    return <SimpleSection data={linkedItem} allowNested={true} />;
  } else if (linkedItem.__typename === "kontent_item_t_and_c_section") {
    return <TAndCSection data={linkedItem} allowNested={true} />;
  } else if (linkedItem.__typename === "kontent_item_external_video") {
    return <ExternalVideo data={linkedItem} />;
  }

  // @todo: Swap the logic
  return null;
};

const resolveTelLinks = (text: string): string => {
  // @note: This should be handled in Kentico but it currently doesn't handle
  // tel: and adds "http://".
  return text?.replace(/http:\/\/tel:/g, "tel:");
};

const resolveLink = (link, domNode) => {
  const Component = domNode.children[0]?.name || "span";
  const to =
    link?.path || (link?.url_slug ? resolveTelLinks(link.url_slug) : "/404");
  const label =
    domNode.children[0].data || domNode.children[0].children[0]?.data;
  return (
    <Link to={to}>
      <Component>{label || to}</Component>
    </Link>
  );
};

const RichText = ({
  codename,
  data,
  settings,
  className,
  hasUnrestyledHeadings
}: Props) => {
  if (!data) {
    return null;
  }

  const { value, modular_content, links } = data;

  if (!checkValueExists(value)) {
    return null;
  }

  return (
    <div
      data-kontent-element-codename={codename}
      className={classnames(styles["RichText"], className)}
    >
      <RichTextElement
        value={resolveTelLinks(value)}
        links={links}
        linkedItems={modular_content}
        resolveLinkedItem={(linkedItem) =>
          resolveLinkedItem(linkedItem, settings)
        }
        resolveLink={resolveLink}
        resolveDomNode={(domNode, domToReact) =>
          resolveDomNode(domNode, domToReact, hasUnrestyledHeadings)
        }
      />
    </div>
  );
};

export default RichText;

export const checkValueExists = (value: RichTextFragmentFragment["value"]) => {
  if (!value) {
    return false;
  }

  return Boolean(value.replace(/<(?!\/?object)[^>]*>/g, ""));
};

export const query = graphql`
  fragment RichTextEmbeddableComponentsFragment on kontent_item {
    ... on kontent_item_single_image {
      ...SingleImageFragment
    }

    ... on kontent_item_double_image {
      ...DoubleImageFragment
    }

    ... on kontent_item_quote {
      ...QuoteFragment
    }

    ... on kontent_item_external_video {
      ...ExternalVideoElementsFragment
    }

    ... on kontent_item_simple_section {
      __typename
      elements {
        overline {
          value
        }
        title {
          value
        }
        body {
          value
          modular_content {
            system {
              codename
            }
            ...LinkFragment
          }
          links {
            url_slug
            link_id
            codename
            type
            path
          }
        }
        align {
          value {
            codename
          }
        }
        background__background {
          value {
            codename
          }
        }
      }
    }

    ... on kontent_item_t_and_c_section {
      elements {
        body {
          value
          modular_content {
            system {
              codename
            }
            ...LinkFragment
          }
          links {
            url_slug
            link_id
            codename
            type
            path
          }
        }
        background_colour {
          value {
            codename
          }
        }
      }
    }
  }

  fragment RichTextFragment on kontent_item_rich_text_element_value {
    value
    modular_content {
      system {
        codename
      }
      ...LinkFragment
      ...RichTextEmbeddableComponentsFragment
    }
    links {
      url_slug
      link_id
      codename
      type
      path
    }
  }
`;
